import PropTypes from 'prop-types';
import React from 'react';
import loadable from '@loadable/component';
import ContentHero from '../Hero';
import ArticleSection from './Article';
import ContentCardSection from './ContentCard';
import CoverFeatureSection from './CoverFeature';
import QuoteSection from './Quote';
import TextSection from './Text';
import SectionHeader from '~/components/Generic/SectionHeader';
import FeaturedArticleCard from '~/components/Article/Card/Blog/Featured';
import InstagramFeed from '~/components/InstagramFeed';
import ContentFaq from '~/components/Content/Faq';
import ContactFooter from '~/components/Footer/ContactSection';
import ShopTileSection from './ShopTile';
import FeaturedModule from './FeaturedCard';
import AppFeature from '../AppFeature';
import { GroupContentContainer } from '~/components/Generic/GroupContent';
import ProductGalleryList from '~/components/Product/GalleryList';
import ActivityPageHeader from '~/components/ActivityPage/Header';
import ActivityExpertSection from '~/components/ActivityPage/ExpertSection';
import { ImageGridContainer } from './ImageGridContainer';
import Site3SectionHeader from '~/components/Generic/SectionHeader/Site3SectionHeader';
import { renderPaddingY } from '~/utils/style-helpers';
import QuickshopSection from '~/components/ActivityPage/QuickshopSection';
import ContentSectionPaddingComponent from './ContentSectionPaddingComponent';
import TeamSalesForm from './TeamSalesform';
import Marquee from '~/components/Generic/Marquee';
import Accolades from './Accolades';
import EmbedForm from './EmbedForm';
import YotpoReviewsWidget from '~/components/Generic/Yotpo/ReviewsWidget';
import ProductAvailabilitySection from './ProductAvailabilityChart/Section';
import ContentCardSlideShow from './ContentCardSlideShow';
import TestimonialSlideShow from '../TestimonialSlideShow';

const GetMediaComponent = ({
  section,
  pageContext,
  page,
  props,
  productSection,
  product,
  selectedVariant,
  pathname,
}) => {
  if ((!section?.media || !section?.media.length) && !/form/.test(section?.slug) && !/yotpo/i.test(section.slug))
    return null;

  if (/form/i.test(section?.slug)) {
    if (section.slug === 'team-sales-form') {
      return <TeamSalesForm />;
    }
    return <EmbedForm description={section.description} />;
  }

  if (productSection && /yotpo/i.test(section.slug)) {
    return <YotpoReviewsWidget product={product} selectedVariant={selectedVariant} pathname={pathname} />;
  }

  if (section.type === 'app-feature') {
    return <AppFeature section={section} />;
  }

  // Multiple media *should* be of the same type
  const [{ __typename }] = section.media || [];

  if (__typename === 'ContentfulImageGrid')
    return (
      <ContentSectionPaddingComponent
        forwardSx={{
          px: [0, '2.4rem', '4rem'],
          mt: ['3rem', '6rem'],
          display: 'flex',
          flexDirection: 'column',
          gap: ['.8rem', null, '2.4rem'],
        }}
      >
        {section.media.map((media) => (
          <ImageGridContainer key={media.id} imageGridData={media} />
        ))}
      </ContentSectionPaddingComponent>
    );

  if (__typename === 'ContentfulHeroFeature') return <ContentHero hero={section.media[0]} />;
  // group in a group
  if (__typename === 'ContentfulProductShowcase')
    return (
      <GroupContentContainer
        groups={section.media}
        type={section.media?.[0].type}
        backgroundColor={section.backgroundColor}
      />
    );

  if (__typename === 'ContentfulProductSpotlight')
    return (
      <ProductGalleryList
        fromProductSection={true}
        presentationalProductCards={section.media}
        backgroundColor={section.backgroundColor}
      />
    );

  if (__typename === 'ContentfulCoverFeature') return <CoverFeatureSection coverFeatures={section.media} />;

  if (section.type && __typename === 'ContentfulAmbassador')
    return (
      <ContentSectionPaddingComponent forwardSx={{ mt: '4.2rem' }}>
        <TestimonialSlideShow content={section.media} type={section.type} textColor={section.textColor} />
      </ContentSectionPaddingComponent>
    );

  if (section.type && __typename === 'ContentfulContentCard')
    return (
      <ContentCardSlideShow
        type={section.type}
        content={section.media}
        CTA={section.ctaButton}
        productSection={productSection}
      />
    );

  if (__typename === 'ContentfulInstagramFeed') return <InstagramFeed media={section.media[0]} />;

  if (__typename === 'ContentfulQuote') return <QuoteSection quotes={section.media} />;

  if (__typename === 'ContentfulContentCard') return <ContentCardSection contentCards={section.media} />;

  if (__typename === 'ContentfulArticle')
    return section.media.length === 1 && section.media[0].isFeatured ? (
      <FeaturedArticleCard article={section.media[0]} />
    ) : (
      <ArticleSection title={section.title} description={section.description} articles={section.media} />
    );

  if (__typename === 'ContentfulFaqTopic')
    return <ContentFaq section={section} fromHomePage={section.media.every((media) => media.homePage)} />;

  if (__typename === 'ContentfulContactCard') return <ContactFooter contact={section} />;

  if (__typename === 'ContentfulShopTile') {
    return (
      <ContentSectionPaddingComponent
        forwardSx={{ px: ['2.4rem', null, '4rem'], mt: ['1.6rem', '4rem'], mb: [0, '3.2rem'] }}
      >
        <ShopTileSection cards={section.media} showNewStyle={section.useNewSectionStyle} />
      </ContentSectionPaddingComponent>
    );
  }

  if (__typename === 'ContentfulCollectionCard') {
    return (
      <ContentSectionPaddingComponent forwardSx={{ pt: ['2.4rem', null, 0], px: ['2.4rem', null, '4rem'] }}>
        <FeaturedModule cards={section.media} title={section.title} subtitle={section.subtitle} />
      </ContentSectionPaddingComponent>
    );
  }

  if (__typename === 'ContentfulActivityPageHeader') {
    return (
      <ActivityPageHeader
        allActivities={pageContext.allActivities}
        section={section.media[0]}
        secondaryColor={page.secondaryColor}
      />
    );
  }

  if (__typename === 'ContentfulActivityExpert') {
    return (
      <ActivityExpertSection
        experts={section.media}
        primaryColor={page.primaryColor}
        secondaryColor={page.secondaryColor}
      />
    );
  }

  if (__typename === 'ContentfulQuickshopSection') {
    return (
      <ContentSectionPaddingComponent forwardSx={{ px: [0, null, '4rem'], mt: ['1.6rem', '4rem'], mb: [0, '3.2rem'] }}>
        <QuickshopSection
          shoppingCollections={section.media?.[0].shoppingCollections}
          activity={pageContext?.slug ?? section?.slug ?? ''}
        />
      </ContentSectionPaddingComponent>
    );
  }

  if (__typename === 'ContentfulProductAvailabilityChart') {
    return <ProductAvailabilitySection charts={section.media} />;
  }

  return null;
};

const ContentSection = React.memo(function ContentSection({
  section,
  hideText,
  forwardSx,
  collectionList,
  productSection,
  pageContext,
  page,
  product,
  selectedVariant,
  pathname,
  ...props
}) {
  const {
    viewMode = 'both',
    media,
    backgroundColor,
    textColor,
    paddingType,
    slug,
    textureImage,
    textureImageMobile,
    useNewSectionStyle: showNewSection,
    paddingTopMobile,
    paddingBottomMobile,
    paddingTopDesktop,
    paddingBottomDesktop,
    type,
  } = section;
  /**
   * Site 3.0 header and section have different style rules. Render the new
   * header and section if the section has the useNewSectionStyle flag
   */

  const isAccoladeSection = /accolades/i.test(slug);
  const isMarquee = slug.includes('marquee');
  const isCollectionModule = media?.[0].__typename === 'ContentfulCollectionCard';
  const isAppFeatureSection = slug === 'app-feature';
  const isTeamSalesForm = slug === 'team-sales-form';
  const isReferences = slug === 'science-references';
  const isEmbeddedForm = /form/i.test(slug) && !isTeamSalesForm;
  const Site3SectionHeaderComponent = (
    <Site3SectionHeader
      {...section}
      color={textColor}
      noMaxWidth={type === 'P2 Featurette'}
      titleSx={type === 'P2 Featurette' ? { mb: '1.6rem', px: ['2.4rem', '12rem'] } : undefined}
    />
  );

  const OldSectionHeaderComponent = (
    <>
      {!isAppFeatureSection &&
      !isAccoladeSection &&
      !isCollectionModule &&
      !hideText &&
      !section.isEditorial &&
      !section.cta &&
      (section.title || section.subtitle || section.description) ? (
        <SectionHeader
          textDirection={section.textDirection}
          textDirectionMobile={section.textDirectionMobile}
          linkSlug={media?.[0]?.slug}
          productSection={productSection}
          title={section.title}
          mobileTitle={section.mobileTitle ?? section.title}
          subtitle={section.subtitle}
          body={section.description}
          forwardSx={forwardSx}
        />
      ) : null}
      {!isAppFeatureSection &&
      !isAccoladeSection &&
      !isCollectionModule &&
      !hideText &&
      (section.isEditorial || section.cta) &&
      (section.subtitle || section.title || section.description) ? (
        <TextSection
          title={section.title}
          mobileTitle={section.mobileTitle ?? section.title}
          subtitle={section.subtitle}
          body={section.description}
          cta={section.cta}
          forwardSx={{ mt: ['7.5rem', '8.5rem', '9.5rem'], ...forwardSx }}
          columnNumber={isReferences ? 1 : 2}
        />
      ) : null}
    </>
  );

  const SectionHeaderComponent = showNewSection ? Site3SectionHeaderComponent : OldSectionHeaderComponent;

  const newSectionStyle =
    type === 'P2 Featurette'
      ? {
          py: ['2.4rem', '4.8rem'],
          my: ['2.4rem', '4.8rem'],
        }
      : {
          pt: [renderPaddingY(paddingTopMobile), renderPaddingY(paddingTopDesktop)],
          pb: [renderPaddingY(paddingBottomMobile), renderPaddingY(paddingBottomDesktop)],
        };

  const oldSectionStyle = {
    p: /doublePadding/i.test(paddingType)
      ? ['4.8rem 0', '9.6rem 0']
      : /singlePadding/i.test(paddingType)
      ? ['4.8rem 0 0 0', '9.6rem 0 0 0']
      : null,
  };

  return (
    <section
      sx={{
        display: /mobile/i.test(viewMode) ? ['block', 'none'] : /desktop/i.test(viewMode) ? ['none', 'block'] : 'block',
        width: '100%',
        height: '100%',
        overflowX: 'clip',
        position: 'relative',
        zIndex: isMarquee ? 50 : null,
        color: textColor ?? 'black',
        isolation: 'isolate',
        scrollMarginTop: 'calc(var(--nav-height) + var(--notification-height))',
        ':before': {
          content: '""',
          top: 0,
          zIndex: -1,
          overflow: 'none',
          position: 'absolute',
          height: '100%',
          width: '100vw',
          backgroundPosition: 'start',
          backgroundSize: ['auto max(100vh, 900px)', 'auto max(100vh, 900px)'],
          backgroundColor: backgroundColor ?? 'transparent',
          backgroundImage: [
            textureImageMobile ? `url(${textureImageMobile.publicUrl}?fm=webp)` : null,
            textureImage ? `url(${textureImage.publicUrl}?fm=webp)` : null,
          ],
        },

        ...(showNewSection ? newSectionStyle : oldSectionStyle),
      }}
      id={isTeamSalesForm || isEmbeddedForm ? section.slug : null}
    >
      {!isEmbeddedForm && SectionHeaderComponent}

      {isAccoladeSection && <Accolades section={section} />}

      {isMarquee && (
        <Marquee
          images={section?.images}
          forwardSX={{
            pt: '24px',
            mb: ['-16px', null, '-32px'],
            pb: 0,
          }}
        />
      )}

      <GetMediaComponent
        section={section}
        props={props}
        collectionList={collectionList}
        pageContext={pageContext}
        page={page}
        productSection={productSection}
        product={product}
        selectedVariant={selectedVariant}
        pathname={pathname}
      />
    </section>
  );
});

ContentSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string,
    isEditorial: PropTypes.bool,
    description: PropTypes.shape({
      description: PropTypes.string,
    }),
    media: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

export default ContentSection;
